
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  getAllReferralIds, 
  getReferralIdBankStats, 
  checkAndReplenishBank, 
  allocateReferralIdsToInstance 
} from '@/utils/referral/bank';
import { ReferralId, ReferralIdBankStats } from '@/utils/referral/bank/types';

export const useSuperAdminReferralBank = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [referralIds, setReferralIds] = useState<ReferralId[]>([]);
  const [bankStats, setBankStats] = useState<ReferralIdBankStats | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load initial data
  const loadData = useCallback(() => {
    setIsLoading(true);
    
    try {
      // Get all referral IDs
      const allIds = getAllReferralIds();
      setReferralIds(allIds);
      
      // Get bank statistics
      const stats = getReferralIdBankStats();
      setBankStats(stats);
    } catch (error) {
      console.error('Error loading referral ID bank data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral ID bank data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Generate more referral IDs - removed functionality
  const handleGenerateMoreIds = async (count: number = 1000) => {
    toast({
      title: 'Permission Denied',
      description: 'Only Master Admins can generate new referral IDs',
      variant: 'destructive',
    });
  };
  
  // Check and replenish bank if needed - removed functionality
  const handleCheckAndReplenish = () => {
    toast({
      title: 'Permission Denied',
      description: 'Only Master Admins can replenish the referral ID bank',
      variant: 'destructive',
    });
  };
  
  // Allocate IDs to an instance - kept to allow super admins to allocate existing IDs
  const handleAllocateToInstance = async (instanceId: string, planLimit: number) => {
    try {
      // Check if there are enough unassigned IDs to allocate
      if (bankStats && bankStats.unassignedIds <= 0) {
        toast({
          title: 'No Available IDs',
          description: 'There are no unassigned referral IDs available. Please contact a Master Admin to generate more IDs.',
          variant: 'destructive',
        });
        return { success: false, allocated: 0, remaining: 0 };
      }
      
      const result = allocateReferralIdsToInstance(instanceId, planLimit);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: `Allocated ${result.allocated} referral IDs to instance. Total allocated: ${result.remaining}`,
        });
        
        // Reload data
        loadData();
        return result;
      }
    } catch (error) {
      console.error('Error allocating referral IDs:', error);
      toast({
        title: 'Error',
        description: 'Failed to allocate referral IDs to instance',
        variant: 'destructive',
      });
    }
    
    return { success: false, allocated: 0, remaining: 0 };
  };
  
  // Filter referral IDs based on search query
  const filteredReferralIds = referralIds.filter(id => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      id.code.toLowerCase().includes(query) ||
      (id.instanceId && id.instanceId.toLowerCase().includes(query))
    );
  });
  
  return {
    isLoading,
    referralIds: filteredReferralIds,
    bankStats,
    isGenerating,
    searchQuery,
    setSearchQuery,
    handleGenerateMoreIds,
    handleCheckAndReplenish,
    handleAllocateToInstance,
    refreshData: loadData
  };
};
